import { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { replace } from 'connected-react-router'
import { replaceWithRegion } from 'actions/NavigationActions'
import type { RouteComponentProps } from 'react-router'
import { useLocation } from 'react-router'
import { useIntl } from 'react-intl'

import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { getCruiseSearchStateFromParams, getCruiseOfferFiltersFromParams } from 'lib/cruises/cruiseUtils'
import { getListName } from 'analytics/enhanced-ecommerce'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { useCruiseDealBySlug } from 'hooks/Cruise/useCruiseDeal'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

import { mediaQueryUp } from 'components/utils/breakpoint'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import CruiseVerticalUSPs from 'components/Cruises/CruiseOfferPage/content/BookWithConfidence/CruiseVerticalUSPs'

import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'LuxKit/Button/TextButton'

import SupportTile from 'components/Common/SupportTile/SupportTile'
import CruiseOfferListNoResults from 'components/Cruises/Common/CruiseOfferListNoResults'

import CruiseSearchFiltersSidebar from '../SearchPage/Filters/CruiseSearchExtraFiltersSidebar'
import CruiseSortDropdown from '../SearchPage/Filters/Inputs/CruiseSortDropdown'
import CruiseHeadData from '../Common/CruiseHeadData'
import CruiseDealHero from './CruiseDealHero'
import CruiseDealRecommendations from './CruiseDealRecommendations'
import CruiseDealFilterChips from './CruiseDealFilterChips'
import CruiseDealSkeleton from './CruiseDealSkeleton'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'

const PageContent = styled.div`
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    display: grid;
    padding: ${rem(16)} 0;
    gap: ${rem(32)};
    grid-template-columns: ${rem(250)} 1fr;
    &.no-filters {
      grid-template-columns: 1fr;
    }
  }
`

const OfferListWrapper = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }
`

interface Props extends RouteComponentProps<{ slug: string }> {}

function CruiseDealPage({ match }: Props) {
  const { slug } = match.params
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const intl = useIntl()

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const initialState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [])
  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const [cruiseDeal, isFetchingCruiseDeal, error] = useCruiseDealBySlug(slug)

  const dealFilters = useMemo(() => ({
    cruiseLines: cruiseDeal ? [cruiseDeal.cruiseLine.code] : [],
    rateCodes: cruiseDeal?.rates.map(rate => rate.code) || [],
    offerDepartureIds: cruiseDeal?.departures?.map(departure => departure.id) || [],
  }), [cruiseDeal])

  const [filters, hasFiltersSelected] = useMemo(() => {
    // Selected filters from the URL
    const [selectedFilters, hasFiltersSelected] = getCruiseOfferFiltersFromParams(queryParams)

    // Merge the selected filters with the deal filters
    const filters = {
      ...selectedFilters,
      ...dealFilters,
    }

    return [filters, hasFiltersSelected]
  }, [queryParams, dealFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Deal Page Offer List'),
    { key: getOfferListKey(filters) },
  )
  const [searchCount, fetchingSearchCount] = useCruiseSearchCount(filters)

  const onSortChange = useCallback((nextSort: App.OfferListSortByOptions) => {
    const nextSearch = setSearchParamValue(search, 'sortBy', nextSort)
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  // Redirect to the search page when the deal has expired
  useEffect(() => {
    const isExpired = cruiseDeal?.endDate && new Date(cruiseDeal.endDate) < new Date()
    const isDealNotFound = !isFetchingCruiseDeal && !cruiseDeal && !!error

    if (isExpired || isDealNotFound) {
      dispatch(replaceWithRegion('/search/cruises'))
    }
  }, [cruiseDeal, isFetchingCruiseDeal, dispatch, error])

  const interstitials = useMemo((): Array<OfferListV2Item> => ([{
    id: 'heading',
    position: 'start',
    show: 'hasOffers',
  }, {
    id: 'empty',
    position: 'start',
    show: 'hasNoOffers',
    element: !searchCount && <CruiseOfferListNoResults />,
  }]), [searchCount])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  return <>
    {!!cruiseDeal && <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <div>
        <CruiseHeadData />
        <CruiseDealHero cruiseDeal={cruiseDeal} />

        <GlobalSearchContextProvider
          state={globalCruisesSearchState}
          dispatch={globalCruisesSearchDispatch}
        >
          <OfferListWrapper>
            <PageContent className={clsx({ 'no-filters': !fetchingSearchCount && !searchCount && !hasFiltersSelected })}>
              {(fetchingSearchCount || !!searchCount || hasFiltersSelected) &&
                <CSSBreakpoint min="tablet">
                  <CruiseSearchFiltersSidebar filters={filters} shouldHidePromotionFilters />
                </CSSBreakpoint>}
              <Group direction="vertical" gap={16}>
                <Pane>
                  <PaneBody mobileSize="sm-x">
                    <Group direction="vertical" gap={16}>
                      <Group direction="vertical" tabletDirection="horizontal" gap={16} horizontalAlign="space-between">
                        <CruiseDealFilterChips filters={filters} onSortChange={handleSortChange} />

                        <CSSBreakpoint min="desktop">
                          <CruiseSortDropdown sort={filters.sortBy ?? 'recommended'} onChange={onSortChange} />
                        </CSSBreakpoint>
                      </Group>

                      {(fetchingSearchCount || !!searchCount || hasFiltersSelected) && <Group
                        direction="horizontal"
                        gap={16}
                        verticalAlign="center"
                      >
                        <BodyText variant="large">
                          {fetchingSearchCount && intl.formatMessage({
                            id: '5Tvh72wL3BfeOsXUoM5o',
                            defaultMessage: 'Searching for cruises',
                            description: 'Loading message while searching for cruises in the cruise section',
                          })}
                          {!fetchingSearchCount && <>{intl.formatMessage({
                            id: '3rQby87UONPzRyZdea3-',
                            defaultMessage: '{count, plural, one {# cruise} other {# cruises}} found',
                            description: 'Number of cruise search results found in the cruise offer or booking section',
                          }, { count: searchCount })}
                          </>}
                        </BodyText>
                        {fetchingSearchCount && <LoadingAnimation size={8} />}
                      </Group>}
                    </Group>
                  </PaneBody>
                </Pane>

                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16} id="cruise-deal-offer-list">
                    <OfferList
                      filters={filters}
                      tileStyle="search"
                      interstitials={interstitials}
                    />
                  </Group>

                </OfferListEventsProvider>

                <Group direction="horizontal" horizontalAlign="center">
                  <TextButton kind="primary" size="large" to="/cruises#deals-carousel">
                    {intl.formatMessage({
                      id: 'PbqWJSEiA0Q2HYN9Z9ah',
                      defaultMessage: 'View all cruise offers',
                      description: 'Button text to view all cruise offers in the cruise offer or booking section',
                    })}
                  </TextButton>
                </Group>
              </Group>
            </PageContent>
          </OfferListWrapper>

          <Pane>
            <PaneBody size="xl-y">
              <LayoutContainer>
                <Heading colour="neutral-one" variant="heading3" align="start">
                  {intl.formatMessage({
                    id: 'BO1ZPzfNouTWaFhekdTQ',
                    defaultMessage: 'Why you should book with us',
                    description: 'Section heading for why book with us section in the cruises section',
                  })}
                </Heading>
                <CruiseVerticalUSPs />
              </LayoutContainer>
            </PaneBody>
          </Pane>

          <Pane>
            <PaneBody size="xl-y">
              <LayoutContainer>
                <SupportTile />
              </LayoutContainer>
            </PaneBody>
          </Pane>

          <CruiseDealRecommendations filters={dealFilters} />
        </GlobalSearchContextProvider>
      </div>
    </AnalyticsPageContext.Provider>}

    {isFetchingCruiseDeal && <CruiseDealSkeleton />}
  </>
}

export default CruiseDealPage
